<template>
  <div class="usage-guide">
    <h3>{{ $t('usage.title') }}</h3>
    <ol class="steps">
      <li>{{ $t('usage.steps.1') }}</li>
      <li>{{ $t('usage.steps.2') }}</li>
      <li>{{ $t('usage.steps.3') }}</li>
    </ol>

    <div class="toggle-row">
      <button class="btn-toggle" @click="expanded = !expanded">
        <span>{{ expanded ? $t('usage.toggleCollapse') : $t('usage.toggleExpand') }}</span>
        <span class="chevron" :class="{ up: expanded }">▾</span>
      </button>
    </div>

    <div class="params" v-if="expanded">
      <h4>{{ $t('usage.params.title') }}</h4>
      <ul>
        <li>
          <strong>{{ $t('usage.params.artStyle') }}</strong>
        </li>
        <li>
          <strong>{{ $t('usage.params.dmc') }}</strong>
        </li>
        <li>
          <strong>{{ $t('usage.params.merge') }}</strong>
        </li>
        <li>
          <strong>{{ $t('usage.params.custom') }}</strong>
        </li>
      </ul>
    </div>

    <div class="downloads" v-if="expanded && hasAny">
      <div class="block" v-if="imageItems.length">
        <h4>{{ $t('usage.downloads.images') }}</h4>
        <div class="file-list">
          <div class="file" v-for="item in imageItems" :key="item.name">
            <img :src="item.url" :alt="item.name" />
            <div class="meta">
              <a :href="item.url" :download="item.name" class="btn-link">{{ item.name }}</a>
            </div>
          </div>
        </div>
      </div>

      <div class="block" v-if="excelItems.length">
        <h4>{{ $t('usage.downloads.excel') }}</h4>
        <ul class="excel-list">
          <li v-for="item in excelItems" :key="item.name">
            <a :href="item.url" :download="item.name" class="btn-link">{{ item.name }}</a>
          </li>
        </ul>
      </div>
    </div>

    <p v-else class="empty-tip" v-if="expanded">{{ $t('usage.empty') }}</p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

// 通过 Vite 的 import.meta.glob 收集示例资源
// 使用 as: 'url' 强制按静态资源 URL 处理，避免被当作 JS 解析
const imageModules = import.meta.glob('../assets/effect/*.{png,jpg,jpeg,gif,webp,bmp,svg}', { eager: true, as: 'url' }) as Record<string, string>
const excelModules = import.meta.glob('../assets/effect/*.{xls,xlsx,csv}', { eager: true, as: 'url' }) as Record<string, string>

type Item = { name: string; url: string }

function toItems(mods: Record<string, string>): Item[] {
  return Object.entries(mods).map(([path, url]) => {
    const name = path.split('/').pop() || path
    return { name, url } as Item
  })
}

const imageItems = toItems(imageModules)
const excelItems = toItems(excelModules)
const hasAny = computed(() => imageItems.length > 0 || excelItems.length > 0)

// 折叠：默认收起，仅展示上方操作步骤
const expanded = ref(false)
</script>

<style scoped>
.usage-guide {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.steps {
  margin: 0.25rem 0 0.5rem;
  padding-left: 1.2rem;
  color: #555;
}

.downloads .block + .block {
  margin-top: 1rem;
}

.file-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
}

.file {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.file img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  background: #f0f0f0;
}

.file .meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  font-size: 0.9rem;
}

.excel-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  padding-left: 0;
  list-style: none;
}

.name {
  color: #333;
}

.btn-link {
  color: #0d6efd;
  text-decoration: none;
}

.btn-link:hover {
  text-decoration: underline;
}

.empty-tip {
  color: #888;
}

.toggle-row {
  display: flex;
  justify-content: flex-end;
}

.btn-toggle {
  cursor: pointer;
  font-size: 0.95rem;
  padding: 6px 10px;
  border: 1px solid #d0d7de;
  border-radius: 999px;
  background: #fff;
  color: #24292f;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-toggle:hover {
  background: #f6f8fa;
}

.chevron {
  transition: transform .15s ease;
}

.chevron.up {
  transform: rotate(180deg);
}

/* 参数说明区块样式优化 */
.params {
  margin-top: 0.5rem;
  padding: 0.75rem 0.9rem;
  background: #f9fafb;
  border: 1px solid #eee;
  border-radius: 8px;
}

.params h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
}

.params ul {
  margin: 0;
  padding-left: 1.2rem;
}

.params li {
  margin: 0.25rem 0;
  line-height: 1.6;
}
</style>

